import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Users, Flame, Zap as ZapBolt, Droplets,
  Bath, BadgeCheck, AlertTriangle, Sparkles
} from "lucide-react";
import SectionLabel from "@/components/layout/SectionLabel";

const STEPS = [
  {
    key: "people",
    title: "How many people live in your home?",
    options: [
      { v: 1, l: "1–2 people" },
      { v: 3, l: "3–4 people" },
      { v: 5, l: "5–6 people" },
      { v: 7, l: "7+ people" },
    ],
    icon: Users,
  },
  {
    key: "system",
    title: "What's your current hot water system?",
    options: [
      { v: "continuous_gas", l: "Continuous gas", icon: Flame },
      { v: "gas_storage", l: "Gas storage", icon: Flame },
      { v: "electric_storage", l: "Electric storage cylinder", icon: ZapBolt },
      { v: "lpg", l: "LPG bottle", icon: Flame },
      { v: "not_sure", l: "Not sure", icon: Sparkles },
    ],
    icon: Flame,
  },
  {
    key: "usage",
    title: "How would you describe your daily hot water usage?",
    options: [
      { v: "low", l: "Low — short showers, no baths" },
      { v: "medium", l: "Medium — typical Kiwi family" },
      { v: "high", l: "High — long showers, frequent baths" },
    ],
    icon: Droplets,
  },
  {
    key: "bathrooms",
    title: "How many bathrooms in your home?",
    options: [
      { v: 1, l: "1 bathroom" },
      { v: 2, l: "2 bathrooms" },
      { v: 3, l: "3+ bathrooms" },
    ],
    icon: Bath,
  },
  {
    key: "timeframe",
    title: "When are you looking to upgrade?",
    options: [
      { v: "ASAP", l: "ASAP" },
      { v: "1 month", l: "Within 1 month" },
      { v: "1-3 months", l: "1–3 months" },
      { v: "Researching", l: "Researching only" },
    ],
    icon: Sparkles,
  },
];

function recommend(answers) {
  const people = answers.people || 3;
  const system = answers.system;
  const usage = answers.usage;
  const bathrooms = answers.bathrooms || 1;
  // 320L if any heavy indicators
  if (people >= 5 || (usage === "high" && bathrooms >= 2) || bathrooms >= 3) {
    return {
      size: "320L",
      title: "NeoPower Black Shield 320L",
      blurb: "Best for larger homes with high hot water demand. Coming soon — join the waitlist via assessment.",
      confidence: "High",
      reasons: [
        `${people >= 5 ? "Household of 5+" : "Multi-bathroom home"}`,
        `${usage === "high" ? "High daily hot water usage" : "Peak-demand storage needed"}`,
      ],
    };
  }
  if (system === "not_sure" || people >= 7) {
    return {
      size: "Site assessment",
      title: "Site assessment recommended",
      blurb: "Your situation needs a quick site visit to size the right system. We'll come out free of charge.",
      confidence: "Medium",
      reasons: ["Unusual demand profile", "Best assessed on-site"],
    };
  }
  return {
    size: "270L",
    title: "NeoPower Black Shield 270L",
    blurb: "The best all-rounder for most Kiwi families — premium build, COP up to 5.4, available now.",
    confidence: "High",
    reasons: [
      "Balanced storage for typical NZ households",
      "Industry-leading efficiency",
    ],
  };
}

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ name: "", email: "", phone: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const total = STEPS.length + 1; // +1 for lead capture
  const isResultStep = step === STEPS.length;

  const pickAndAdvance = (key, v) => {
    setAnswers((s) => ({ ...s, [key]: v }));
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!lead.name || !lead.email || !lead.consent) {
      return toast.error("Name, email and consent are required.");
    }
    setSubmitting(true);
    try {
      const rec = recommend(answers);
      await submitLead({
        type: "quiz",
        name: lead.name, email: lead.email, phone: lead.phone,
        current_system: answers.system,
        household_size: typeof answers.people === "number" ? answers.people : undefined,
        bathrooms: typeof answers.bathrooms === "number" ? answers.bathrooms : undefined,
        timeframe: answers.timeframe,
        recommended_system: rec.size,
        consent: lead.consent,
        notes: `Quiz answers: ${JSON.stringify(answers)}`,
      });
      setResult(rec);
    } catch {
      toast.error("Submission failed. Please retry.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16">
          <SectionLabel>Quick quiz</SectionLabel>
          <h1 className="font-heading mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-[#0B132B]">
            Which hot water system suits your home?
          </h1>
          <p className="mt-4 text-lg text-[#475569] max-w-2xl">
            5 quick questions. No long forms. Get a personalised recommendation in under a minute.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-2xl mx-auto px-6 sm:px-8">
          <Card data-testid="quiz-card" className="rounded-3xl p-6 sm:p-10 border-[#E2E8F0] bg-white">
            {!result && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-xs uppercase tracking-widest font-semibold text-[#475569]">
                    Question {Math.min(step + 1, total)} of {total}
                  </p>
                  <span className="text-xs text-[#475569]">{Math.round(((step + 1) / total) * 100)}%</span>
                </div>
                <Progress value={((step + 1) / total) * 100} className="h-1.5 mb-8" />

                {!isResultStep && (
                  <div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B132B]">
                      {STEPS[step].title}
                    </h2>
                    <div className="mt-6 grid sm:grid-cols-2 gap-3">
                      {STEPS[step].options.map((o) => {
                        const Icon = o.icon || STEPS[step].icon;
                        return (
                          <button
                            key={String(o.v)}
                            data-testid={`quiz-option-${STEPS[step].key}-${o.v}`}
                            onClick={() => pickAndAdvance(STEPS[step].key, o.v)}
                            className="text-left p-5 rounded-2xl border-2 border-[#E2E8F0] hover:border-[#10B981] hover:bg-[#10B981]/5 transition-all"
                          >
                            <Icon className="h-5 w-5 mb-2 text-[#10B981]" />
                            <p className="font-heading font-semibold text-[#0B132B]">{o.l}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isResultStep && (
                  <div className="space-y-4">
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B132B]">
                      Almost done — where can we send your result?
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <div><Label className="text-xs">Name *</Label><Input data-testid="quiz-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} /></div>
                      <div><Label className="text-xs">Email *</Label><Input data-testid="quiz-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} /></div>
                      <div className="sm:col-span-2"><Label className="text-xs">Phone (optional)</Label><Input data-testid="quiz-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} /></div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
                      <Checkbox data-testid="quiz-consent" id="qz-c" checked={lead.consent} onCheckedChange={(v) => setLead({ ...lead, consent: !!v })} />
                      <Label htmlFor="qz-c" className="text-sm cursor-pointer leading-relaxed">
                        I agree for AeroMax Energy to contact me with my recommendation.
                      </Label>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep((s) => Math.max(s - 1, 0))}
                    disabled={step === 0}
                    data-testid="quiz-prev"
                    className="rounded-full px-6 py-5 border-[#E2E8F0]"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  {isResultStep && (
                    <Button
                      onClick={submit}
                      data-testid="quiz-submit"
                      disabled={submitting}
                      className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-5 font-semibold"
                    >
                      {submitting ? "Saving..." : "Show My Recommendation"} <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </>
            )}

            {result && (
              <div data-testid="quiz-result" className="space-y-5">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981]">
                  <BadgeCheck className="h-6 w-6" />
                </div>
                <h2 className="font-heading text-3xl font-bold text-[#0B132B]">{result.title}</h2>
                <p className="text-[#475569]">{result.blurb}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-[#E2E8F0] p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">Recommended</p>
                    <p className="font-heading text-2xl font-bold text-[#0B132B] mt-1">{result.size}</p>
                  </div>
                  <div className="rounded-2xl border border-[#E2E8F0] p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">Confidence</p>
                    <p className="font-heading text-2xl font-bold text-[#0B132B] mt-1">{result.confidence}</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-[#475569]">
                  {result.reasons.map((r, i) => (
                    <li key={i} className="flex items-center gap-2">
                      {result.size === "Site assessment" ? <AlertTriangle className="h-4 w-4 text-[#2563EB]" /> : <BadgeCheck className="h-4 w-4 text-[#10B981]" />}
                      {r}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/free-home-assessment" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3.5 font-semibold">
                    Book Free Assessment <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/savings-calculator" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white px-6 py-3.5 font-semibold transition">
                    Run the Savings Calculator
                  </Link>
                </div>
              </div>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}
